/**
 * Passage annotation — pure logic. No DOM, no React.
 *
 * The student picks one of four marking tools — circle, box, underline, bracket — and drags
 * across the words they want to mark, the way they would work a printed exam paper. A mark is
 * a word-aligned [start, end) range into the passage string plus the shape drawn over it.
 *
 * PHASE 1 SCOPE: marks are counted, never graded. There is no expected-answer plan, no
 * threshold, and nothing here blocks the student from starting the questions. The only thing
 * that leaves the device is the per-shape tally from countByShape() — see the annotation count
 * on the session record. Everything in this module is deliberately free of any notion of a
 * "correct" mark.
 *
 * WHY MARKS ARE NOT HIGHLIGHTS. The highlighter (see passageHighlights.ts) keeps one sorted,
 * merged, non-overlapping range list, because two overlapping highlights of the same colour are
 * indistinguishable from one. Marks are the opposite: a student may bracket a whole sentence
 * AND underline three words inside it, and both must survive. So marks are discrete, identified
 * entities that never merge, stored newest-last — which is also the erase order.
 *
 * SINGLE LINE PER MARK. Every mark is clamped to the physical line its drag began on. PSLE
 * passages number fixed physical lines and a circle drawn across a line break reads as a
 * mistake rather than as a mark. Highlights bridge lines; marks do not.
 *
 * The tokenizer, word snapping, and line-number handling are reused wholesale from
 * passageHighlights.ts — a mark and a highlight agree on what a word is.
 */

import { snapRange, type Token } from '@/lib/passageHighlights';

/**
 * The marking tools, in toolbar order.
 *
 * The full symbol set taught to Singapore primary students (Si Ling Primary School annotation
 * sheet), so a child meets the same marks here that their teacher uses:
 *
 *   circle        — the characters (who)
 *   box           — the setting and the time (where and when)
 *   roundBracket  — the problem (what), underlined inside round brackets
 *   squareBracket — the solution (what), underlined inside square brackets
 *   underline     — plain emphasis
 *   squiggly      — words the student does not understand
 *   question      — a circled "?" — something the student wants to ask
 *   remind        — a circled "R" — what the text reminds them of
 *   surprise      — a circled "!" — surprising information
 *   link          — a double-headed arrow joining a pronoun to its referent
 *
 * The last four are drawn differently but stored the same way, which is what keeps one gesture,
 * one store and one counting path serving everything:
 *
 *   The three MARKERS are single glyphs. On paper they live in the margin; the passage sheet has
 *   no margin (text runs to the edge and scrolls sideways), so each one anchors to the words it
 *   is about and is drawn in the line's leading space just above them.
 *
 *   LINK is the one mark with two ends. It carries a second range, `targetStart`/`targetEnd`,
 *   and is built in two steps: the student picks the pronoun, then picks what it refers to.
 */
export type AnnotationShape =
  | 'circle'
  | 'box'
  | 'roundBracket'
  | 'squareBracket'
  | 'underline'
  | 'squiggly'
  | 'question'
  | 'remind'
  | 'surprise'
  | 'link';

export const ANNOTATION_SHAPE_ORDER: AnnotationShape[] = [
  'circle',
  'box',
  'roundBracket',
  'squareBracket',
  'underline',
  'squiggly',
  'link',
  'question',
  'remind',
  'surprise',
];

/** Marks built from two selections rather than one. */
export function isTwoEndedShape(shape: AnnotationShape): boolean {
  return shape === 'link';
}

const SHAPE_SET = new Set<string>(ANNOTATION_SHAPE_ORDER);

export function isAnnotationShape(value: unknown): value is AnnotationShape {
  return typeof value === 'string' && SHAPE_SET.has(value);
}

/** A single mark over [start, end) character offsets of the passage string. */
export interface AnnotationMark {
  /** Unique within a session. Also the erase handle — marks are never merged. */
  id: number;
  shape: AnnotationShape;
  start: number;
  /** Exclusive. */
  end: number;
  /** The far end of a `link`. Absent on every other shape. */
  targetStart?: number;
  /** Exclusive. */
  targetEnd?: number;
}

/** The word range a pending link is waiting to be joined to. */
export interface PendingLink {
  start: number;
  end: number;
}

export const ANNOTATION_STORAGE_KEY = 'thinkotter:passage-annotations:v1';

/** Cap on how many sessions' marks we keep in localStorage. Mirrors the highlighter. */
export const MAX_STORED_SESSIONS = 20;

/**
 * Safety valve on a single session, so a child holding a finger down cannot exhaust the
 * localStorage quota. Far above any real annotation of a 350-word passage.
 */
export const MAX_MARKS_PER_SESSION = 200;

// ---------------------------------------------------------------------------
// Lines
// ---------------------------------------------------------------------------

/**
 * The physical line index a word offset sits on, or -1 if no token starts there.
 *
 * "Physical" is the same sense the passage uses everywhere else: lines separated by newline
 * tokens, which under `whitespace-pre` are exactly the lines the student sees and the lines the
 * "1." / "5." prefixes number.
 */
export function lineIndexAt(tokens: Token[], offset: number): number {
  let line = 0;
  for (const token of tokens) {
    if (token.type === 'newline') {
      line += 1;
      continue;
    }
    if (token.start === offset) return line;
  }
  return -1;
}

/**
 * Word-align a drag into a mark range, clamped to the line the drag STARTED on.
 *
 * `anchorOffset` is where the pointer went down and `focusOffset` where it ended, in either
 * order on screen but not interchangeable here — the anchor decides the line. Returns null when
 * either end names something unmarkable (a line-number prefix), or when the focus has left the
 * anchor's line, which is how a vertical drag becomes a no-op rather than a mark spanning
 * halfway down the passage.
 */
export function snapMarkRange(
  tokens: Token[],
  anchorOffset: number,
  focusOffset: number
): { start: number; end: number } | null {
  const anchorLine = lineIndexAt(tokens, anchorOffset);
  if (anchorLine < 0) return null;
  if (lineIndexAt(tokens, focusOffset) !== anchorLine) return null;

  return snapRange(tokens, anchorOffset, focusOffset);
}

// ---------------------------------------------------------------------------
// Mark list
// ---------------------------------------------------------------------------

/**
 * Append a mark. Marks never merge and never overwrite, so ordering is purely chronological —
 * which is what makes "the last one you drew is the first one you erase" true.
 */
export function addMark(marks: AnnotationMark[], mark: AnnotationMark): AnnotationMark[] {
  if (mark.end <= mark.start) return marks;
  if (marks.length >= MAX_MARKS_PER_SESSION) return marks;
  return [...marks, mark];
}

/**
 * The mark covering `offset`, or null. Searched newest-first so a tap erases the mark drawn
 * most recently — the one visually on top, and the one the student is most likely aiming at.
 *
 * A link is covered at BOTH its ends, so tapping either the pronoun or its referent removes the
 * arrow. Anything else would leave the student poking at one end wondering why nothing happens.
 */
export function markAt(marks: AnnotationMark[], offset: number): AnnotationMark | null {
  for (let i = marks.length - 1; i >= 0; i -= 1) {
    const mark = marks[i];
    if (offset >= mark.start && offset < mark.end) return mark;
    if (
      mark.targetStart !== undefined &&
      mark.targetEnd !== undefined &&
      offset >= mark.targetStart &&
      offset < mark.targetEnd
    ) {
      return mark;
    }
  }
  return null;
}

export function removeMark(marks: AnnotationMark[], id: number): AnnotationMark[] {
  return marks.filter((mark) => mark.id !== id);
}

/** Drop the most recently added mark. Undo. */
export function removeLastMark(marks: AnnotationMark[]): AnnotationMark[] {
  return marks.slice(0, -1);
}

/** The next free id for a session, so ids stay unique across a reload. */
export function nextMarkId(marks: AnnotationMark[]): number {
  return marks.reduce((max, mark) => Math.max(max, mark.id), 0) + 1;
}

// ---------------------------------------------------------------------------
// Counting — the whole of what Phase 1 measures
// ---------------------------------------------------------------------------

export type AnnotationCounts = Record<AnnotationShape, number> & { total: number };

export const EMPTY_ANNOTATION_COUNTS: AnnotationCounts = Object.freeze({
  circle: 0,
  box: 0,
  roundBracket: 0,
  squareBracket: 0,
  underline: 0,
  squiggly: 0,
  question: 0,
  remind: 0,
  surprise: 0,
  link: 0,
  total: 0,
});

/**
 * Tally marks by shape.
 *
 * This is the only annotation data that ever leaves the device. It carries no offsets and no
 * passage text — just how many of each shape the student drew — which is what lets Phase 1
 * answer "do children use this?" without recording what a child marked or where.
 */
export function countByShape(marks: AnnotationMark[]): AnnotationCounts {
  const counts: AnnotationCounts = { ...EMPTY_ANNOTATION_COUNTS };
  for (const mark of marks) {
    counts[mark.shape] += 1;
    counts.total += 1;
  }
  return counts;
}

// ---------------------------------------------------------------------------
// Storage serialization
// ---------------------------------------------------------------------------

/**
 * Drop anything that cannot apply to a passage of `length` characters, along with unrecognised
 * shapes and duplicate ids. Guards against a payload stored against a different passage, or a
 * hand-edited localStorage value.
 */
export function clampMarks(marks: unknown, length: number): AnnotationMark[] {
  if (!Array.isArray(marks)) return [];

  const seen = new Set<number>();
  const valid: AnnotationMark[] = [];

  const inBounds = (from: unknown, to: unknown): boolean =>
    Number.isInteger(from) &&
    Number.isInteger(to) &&
    (from as number) >= 0 &&
    (to as number) <= length &&
    (to as number) > (from as number);

  for (const entry of marks) {
    if (!entry || typeof entry !== 'object') continue;
    const { id, shape, start, end, targetStart, targetEnd } = entry as Partial<AnnotationMark>;

    if (
      !Number.isInteger(id) ||
      !isAnnotationShape(shape) ||
      !inBounds(start, end) ||
      seen.has(id as number)
    ) {
      continue;
    }

    // A link is meaningless with only one end, so an incomplete one is dropped rather than
    // rendered as a mark that points nowhere.
    const twoEnded = isTwoEndedShape(shape);
    if (twoEnded && !inBounds(targetStart, targetEnd)) continue;

    seen.add(id as number);
    valid.push({
      id: id as number,
      shape,
      start: start as number,
      end: end as number,
      ...(twoEnded ? { targetStart: targetStart as number, targetEnd: targetEnd as number } : {}),
    });
    if (valid.length >= MAX_MARKS_PER_SESSION) break;
  }

  return valid;
}

export interface StoredAnnotationSession {
  marks: AnnotationMark[];
  updatedAt: number;
}

export type AnnotationStoreData = Record<string, StoredAnnotationSession>;

/** Tolerant parse: any malformed payload degrades to "no marks" rather than throwing. */
export function parseAnnotationStore(raw: string | null): AnnotationStoreData {
  if (!raw) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

  const result: AnnotationStoreData = {};
  for (const [sessionId, entry] of Object.entries(parsed as Record<string, unknown>)) {
    if (!entry || typeof entry !== 'object') continue;
    const { marks, updatedAt } = entry as Partial<StoredAnnotationSession>;
    if (!Array.isArray(marks)) continue;
    result[sessionId] = {
      // Bound-checking happens later in clampMarks(), once the passage length is known.
      marks: marks.filter((m) => m && isAnnotationShape((m as AnnotationMark).shape)),
      updatedAt: typeof updatedAt === 'number' ? updatedAt : 0,
    };
  }

  return result;
}

/** Keep only the `max` most recently touched sessions, so the key cannot grow forever. */
export function pruneToRecent(data: AnnotationStoreData, max: number): AnnotationStoreData {
  const entries = Object.entries(data);
  if (entries.length <= max) return data;

  const kept = entries.sort(([, a], [, b]) => b.updatedAt - a.updatedAt).slice(0, max);
  return Object.fromEntries(kept);
}
